import React, { useState, useEffect } from 'react';
import { db } from '../db';
import { toast, confirmDialog } from '../utils/feedback';
import { Plus, Play, Trash2, ArrowLeft, CheckCircle, ListChecks, X, ArrowDown, Timer, Square, ArrowRight, Edit2, Calculator, RefreshCw, Share2, Info, Sparkles, Link , Flame, ArrowLeftRight, TrendingDown, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getEarnedBadges } from '../utils/badgeUtils';
import { ExercisePreviewModal, ExerciseSelectorModal } from '../components/ExerciseSelectorModal';
import { generateWorkoutAnalysis } from '../services/aiService';

import PlateCalculatorModal from '../components/PlateCalculatorModal';
import SmartBuilderModal from '../components/SmartBuilderModal';
import Loader from '../components/Loader';
import { isCardioExercise, formatSetDisplay, getCardioType, getCardioFields, calculateWorkoutVolume } from '../utils/exerciseUtils';

const Routines = ({ currentProfile }) => {
  const [routines, setRoutines] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [pastWorkouts, setPastWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastAiInsight, setLastAiInsight] = useState(null);
  
  const [view, setView] = useState('list'); // 'list', 'create', 'logger'
  
  // Create/Edit Routine State
  const [newRoutineName, setNewRoutineName] = useState('');
  const [selectedExercises, setSelectedExercises] = useState([]); // now contains { type: 'single', ... } and { type: 'superset', items: [...] }
  const [muscleFilter, setMuscleFilter] = useState([]);
  const [editingRoutineId, setEditingRoutineId] = useState(null);
  const [previewExercise, setPreviewExercise] = useState(null);
  const [previewAddedExercise, setPreviewAddedExercise] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);

  const handleShareRoutine = (routine) => {
    navigate('/forum', { state: { sharedRoutine: routine } });
  };

  const MUSCLE_CATEGORIES = [
    'Épaules', 'Biceps', 'Triceps', 'Avant-bras', 'Dos', 'Pectoraux', 
    'Abdos', 'Quadriceps', 'Ischio-jambiers', 'Mollets', 'Cou', 'Fessier', 'Cardio'
  ];

  const getMuscles = (ex) => {
    let rawMuscles = ex.muscles || (ex.muscle ? [ex.muscle] : null);
    const lowerName = (ex.name || '').toLowerCase();
    const isCardioName = /(cardio|course|tapis|\bvélo\b|\bvelo\b|elliptique|rameur|\bescalier|\bescaliers|stairmaster|\bcorde à sauter|marche|spinning|hiit|ergo|cycling|running|treadmill|rowing|jogging|stepper|assault bike|ski erg)/i.test(lowerName);

    if (!rawMuscles) return isCardioName ? ['Cardio'] : ['Autre'];
    const arr = Array.isArray(rawMuscles) ? rawMuscles : [rawMuscles];
    const mapped = arr.map(m => {
      if (m === 'Jambes') {
        if (isCardioName) return 'Cardio';
        if (/(curl|ischio|soulevé|souleve|deadlift|sdt|good morning|rdl|arrière cuisse|arriere cuisse)/.test(lowerName)) return 'Ischio-jambiers';
        if (/(hip thrust|fessier|glute|abduct)/.test(lowerName)) return 'Fessier';
        return 'Quadriceps';
      }
      return m;
    });
    if (isCardioName && !mapped.includes('Cardio')) {
      mapped.push('Cardio');
    }
    return mapped;
  };

  const formatWorkoutDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Smart Logger State
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showFeelingModal, setShowFeelingModal] = useState(false);

  // Timer State
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const isTimerRunning = !!timerStartTime;

  // Celebration State
  const [newPRs, setNewPRs] = useState([]);
  const [newBadges, setNewBadges] = useState([]);
  const [isSavingRoutine, setIsSavingRoutine] = useState(false);
  
  // Plate Calculator State
  const [showPlateCalc, setShowPlateCalc] = useState(false);
  const [plateCalcTargetWeight, setPlateCalcTargetWeight] = useState(60);
  const [plateCalcExerciseId, setPlateCalcExerciseId] = useState(null);
  const [plateCalcBarWeight, setPlateCalcBarWeight] = useState(20);
  
  // Smart Builder State
  const [showSmartBuilder, setShowSmartBuilder] = useState(false);

  const handleOpenPlateCalc = (weight, exerciseId) => {
    setPlateCalcTargetWeight(weight || 60);
    setPlateCalcExerciseId(exerciseId);
    
    const exerciseDef = allExercises.find(e => e.id === exerciseId);
    const equipment = exerciseDef ? exerciseDef.equipment : 'Autre';
    const dbEmptyWeight = exerciseDef && exerciseDef.emptyWeight ? Number(exerciseDef.emptyWeight) : (equipment === 'Barre' ? 20 : 0);
    
    setPlateCalcBarWeight(dbEmptyWeight);
    setShowPlateCalc(true);
  };

  const navigate = useNavigate();

  const loadData = async () => {
    if (!currentProfile) return;
    setLoading(true);
    try {
      const [rData, eData, wData] = await Promise.all([
        db.getRoutines(currentProfile.id),
        db.getExercises(currentProfile.id),
        db.getWorkouts(currentProfile.id)
      ]);
      
      setRoutines(rData || []);
      
      const safeEData = eData || [];
      safeEData.sort((a, b) => {
        const muscleA = String(a.muscle || (a.muscles && a.muscles[0]) || 'Autre');
        const muscleB = String(b.muscle || (b.muscles && b.muscles[0]) || 'Autre');
        const nameA = String(a.name || '');
        const nameB = String(b.name || '');
        return muscleA.localeCompare(muscleB) || nameA.localeCompare(nameB);
      });
      
      setAllExercises(safeEData);
      setPastWorkouts(wData || []);
    } catch (error) {
      console.error("Erreur lors du chargement des programmes :", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentProfile?.id]);

  // Load active workout from localStorage
  useEffect(() => {
    if (currentProfile && !activeWorkout && view === 'list') {
      const saved = localStorage.getItem(`activeWorkout_${currentProfile.id}`);
      if (saved) {
        setActiveWorkout(JSON.parse(saved));
        setView('logger');
      }
    }
  }, [currentProfile, view]);

  // Save active workout to localStorage
  useEffect(() => {
    if (activeWorkout && currentProfile) {
      localStorage.setItem(`activeWorkout_${currentProfile.id}`, JSON.stringify(activeWorkout));
    }
  }, [activeWorkout, currentProfile]);

  // Timer logic
  useEffect(() => {
    let interval = null;
    if (timerStartTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - timerStartTime) / 1000);
        if (elapsed > 15 * 60) {
          setTimerSeconds(0);
          setTimerStartTime(null);
        } else {
          setTimerSeconds(elapsed);
        }
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerStartTime]);

  const toggleTimer = () => {
    if (isTimerRunning) {
      setTimerStartTime(null);
    } else {
      setTimerSeconds(0);
      setTimerStartTime(Date.now());
    }
  };

  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const handleReplaceExercise = (ex) => {
    if (!replaceTarget || !activeWorkout) return;
    
    const newWorkout = { ...activeWorkout };
    newWorkout.exercises = [...newWorkout.exercises];
    
    const isCardio = isCardioExercise(ex, allExercises);
    const defaultSet = isCardio 
      ? { duration: '', distance: '', reps: '', weight: '', isUnilateral: false, isWarmup: false, isDropSet: false }
      : { reps: '', weight: '', repsL: '', repsR: '', weightL: '', weightR: '', isUnilateral: false, isWarmup: false, isDropSet: false };

    if (replaceTarget.isAdding) {
      newWorkout.exercises.push({
        type: 'single',
        exerciseId: ex.id,
        name: ex.name,
        equipment: ex.equipment || 'Autre',
        sets: [{ ...defaultSet }]
      });
      setActiveWorkout(newWorkout);
      setReplaceTarget(null);
      return;
    }

    const block = { ...newWorkout.exercises[replaceTarget.blockIndex] };
    
    if (block.type === 'superset') {
      block.type = 'single';
      block.exerciseId = ex.id;
      block.name = ex.name;
      block.equipment = ex.equipment || 'Autre';
      block.sets = [{ ...defaultSet }];
      delete block.items;
    } else {
      block.exerciseId = ex.id;
      block.name = ex.name;
      block.equipment = ex.equipment || 'Autre';
      block.sets = [{ ...defaultSet }];
    }
    
    newWorkout.exercises[replaceTarget.blockIndex] = block;
    setActiveWorkout(newWorkout);
    setReplaceTarget(null);
  };

  // --- CREATION LOGIC ---
  const handleSaveRoutine = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSavingRoutine) return;
    if (!newRoutineName.trim() || selectedExercises.length === 0) return;
    
    setIsSavingRoutine(true);
    try {
      if (editingRoutineId) {
        await db.updateRoutine(editingRoutineId, {
          profileId: currentProfile.id,
          name: newRoutineName.trim(),
          exercises: selectedExercises
        });
        toast.success("Programme mis à jour avec succès !");
      } else {
        await db.addRoutine({
          profileId: currentProfile.id,
          name: newRoutineName.trim(),
          exercises: selectedExercises
        });
        toast.success("Programme enregistré avec succès !");
      }
      
      setView('list');
      setNewRoutineName('');
      setSelectedExercises([]);
      setEditingRoutineId(null);
      loadData();
    } catch (err) {
      console.error("Erreur enregistrement routine:", err);
      toast.error("Erreur lors de l'enregistrement du programme.");
    } finally {
      setIsSavingRoutine(false);
    }
  };

  const startEditRoutine = (routine) => {
    setEditingRoutineId(routine.id);
    setNewRoutineName(routine.name);
    // Deep copy exercises to generate unique block ids for the editor
    const copiedExercises = routine.exercises.map(block => {
      // Wrapper for old routine formats without type
      const type = block.type || 'single';
      if (type === 'superset') {
        return { id: Date.now() + Math.random(), type: 'superset', items: [...block.items] };
      } else {
        return { id: Date.now() + Math.random(), type: 'single', exerciseId: block.exerciseId, name: block.name, equipment: block.equipment };
      }
    });
    setSelectedExercises(copiedExercises);
    setView('create');
  };

  const addExerciseToSelection = (ex) => {
    setSelectedExercises([...selectedExercises, { id: Date.now() + Math.random(), type: 'single', exerciseId: ex.id, name: ex.name, equipment: ex.equipment }]);
  };

  const removeSelectedBlock = (index) => {
    const newSel = [...selectedExercises];
    newSel.splice(index, 1);
    setSelectedExercises(newSel);
  };

  const moveBlockUp = (index) => {
    if (index === 0) return;
    const newSel = [...selectedExercises];
    const temp = newSel[index];
    newSel[index] = newSel[index - 1];
    newSel[index - 1] = temp;
    setSelectedExercises(newSel);
  };

  const moveBlockDown = (index) => {
    if (index === selectedExercises.length - 1) return;
    const newSel = [...selectedExercises];
    const temp = newSel[index];
    newSel[index] = newSel[index + 1];
    newSel[index + 1] = temp;
    setSelectedExercises(newSel);
  };

  const linkToPrevious = (index) => {
    if (index === 0) return;
    const newSel = [...selectedExercises];
    const current = newSel[index];
    const prev = newSel[index - 1];

    let supersetItems = [];
    if (prev.type === 'superset') {
      supersetItems = [...prev.items];
    } else {
      supersetItems = [{ exerciseId: prev.exerciseId, name: prev.name, equipment: prev.equipment }];
    }

    if (current.type === 'superset') {
      supersetItems = [...supersetItems, ...current.items];
    } else {
      supersetItems.push({ exerciseId: current.exerciseId, name: current.name, equipment: current.equipment });
    }

    newSel[index - 1] = { id: prev.id, type: 'superset', items: supersetItems };
    newSel.splice(index, 1);
    setSelectedExercises(newSel);
  };

  const unlinkSuperset = (index) => {
    const newSel = [...selectedExercises];
    const block = newSel[index];
    if (block.type !== 'superset') return;
    
    // Split into individual singles
    const newBlocks = block.items.map((item, i) => ({
      id: block.id + i,
      type: 'single',
      exerciseId: item.exerciseId,
      name: item.name,
      equipment: item.equipment
    }));
    
    newSel.splice(index, 1, ...newBlocks);
    setSelectedExercises(newSel);
  };

  // --- SMART LOGGER LOGIC ---
  const getPreviousPerformance = (exerciseId) => {
    for (let w of pastWorkouts) {
      for (let block of w.exercises) {
        if (block.type === 'superset') {
          const ex = block.items.find(e => e.exerciseId === exerciseId);
          if (ex && ex.sets && ex.sets.length > 0) return ex.sets;
        } else {
          if (block.exerciseId === exerciseId && block.sets && block.sets.length > 0) return block.sets;
        }
      }
    }
    return null;
  };

  const getExercisePR = (exerciseId) => {
    let best = { weight: 0, reps: 0 };
    for (let w of pastWorkouts) {
      for (let block of w.exercises) {
        let setsToCheck = [];
        if (block.type === 'superset') {
          const ex = block.items.find(e => String(e.exerciseId) === String(exerciseId));
          if (ex && ex.sets) setsToCheck = ex.sets;
        } else {
          if (String(block.exerciseId) === String(exerciseId) && block.sets) setsToCheck = block.sets;
        }
        
        setsToCheck.forEach(set => {
          if (set.isWarmup) return;
          if (set.isUnilateral) {
            const wL = parseFloat(set.weightL) || 0;
            const rL = parseInt(set.repsL) || 0;
            const wR = parseFloat(set.weightR) || 0;
            const rR = parseInt(set.repsR) || 0;
            if (rL >= 5 && (wL > best.weight || (wL === best.weight && rL > best.reps))) {
              best = { weight: wL, reps: rL };
            }
            if (rR >= 5 && (wR > best.weight || (wR === best.weight && rR > best.reps))) {
              best = { weight: wR, reps: rR };
            }
          } else {
            const wgt = parseFloat(set.weight) || 0;
            const reps = parseInt(set.reps) || 0;
            if (reps >= 5 && (wgt > best.weight || (wgt === best.weight && reps > best.reps))) {
              best = { weight: wgt, reps };
            }
          }
        });
      }
    }
    return best;
  };

  const startRoutine = (routine) => {
    const getPreviousPerformance = (exerciseId) => {
      for (let w of pastWorkouts) {
        for (let ex of w.exercises) {
          if (ex.type === 'superset') {
            const match = ex.items.find(i => String(i.exerciseId) === String(exerciseId));
            if (match && match.sets && match.sets.length > 0) return match.sets;
          } else {
            if (String(ex.exerciseId) === String(exerciseId) && ex.sets && ex.sets.length > 0) return ex.sets;
          }
        }
      }
      return null;
    };

    const buildInitialSets = (exBlock, prevSets) => {
      const isCardio = isCardioExercise(exBlock, allExercises);
      const cardioType = isCardio ? getCardioType(exBlock.name) : 'generic';

      let maxWeight = '';
      let maxWeightL = '';
      let maxWeightR = '';
      let bestPrevSet = null;
      if (prevSets && prevSets.length > 0) {
        const weights = prevSets.map(p => parseFloat(p.weight)).filter(w => !isNaN(w));
        if (weights.length > 0) maxWeight = Math.max(...weights).toString();
        const weightsL = prevSets.map(p => parseFloat(p.weightL)).filter(w => !isNaN(w));
        if (weightsL.length > 0) maxWeightL = Math.max(...weightsL).toString();
        const weightsR = prevSets.map(p => parseFloat(p.weightR)).filter(w => !isNaN(w));
        if (weightsR.length > 0) maxWeightR = Math.max(...weightsR).toString();
        
        let maxW = -1;
        let maxR = -1;
        prevSets.forEach(s => {
          if (s.isWarmup) return;
          const w = parseFloat(s.weight) || parseFloat(s.weightL) || parseFloat(s.distance) || 0;
          const r = parseInt(s.reps) || parseInt(s.repsL) || parseInt(s.duration) || 0;
          if (w > maxW || (w === maxW && r > maxR)) {
            maxW = w;
            maxR = r;
            bestPrevSet = s;
          }
        });
        if (!bestPrevSet) bestPrevSet = prevSets[prevSets.length - 1];
      }

      // If routine explicitly asks for multiple sets (e.g. AI), keep them. Otherwise just 1.
      const targetCount = exBlock.sets && exBlock.sets.length > 1 ? exBlock.sets.length : 1;
      
      return Array.from({ length: targetCount }, (_, i) => {
        const p = prevSets ? (prevSets[i] || prevSets[prevSets.length - 1]) : null;
        const bSet = exBlock.sets && exBlock.sets[i] ? exBlock.sets[i] : {};
        
        let prevText = null;
        const pForText = bestPrevSet || p;
        if (pForText) {
          if (isCardio) {
            prevText = formatSetDisplay(pForText, true, cardioType);
          } else {
            prevText = `${pForText.isDropSet ? '↳ ' : ''}${pForText.isUnilateral ? (pForText.repsL+'/'+pForText.repsR+'x'+pForText.weightL+'kg') : (pForText.reps+'x'+pForText.weight+'kg')}`;
          }
        }

        if (isCardio) {
          return {
            ...bSet,
            cardioType,
            isUnilateral: false,
            duration: '',
            distance: (p && p.distance) ? p.distance : (p && p.weight > 0 ? p.weight.toString() : ''),
            reps: '',
            weight: '',
            incline: p ? (p.incline || '') : '',
            speed: p ? (p.speed || '') : '',
            intensity: p ? (p.intensity || '') : '',
            floors: p ? (p.floors || '') : '',
            isDropSet: false,
            isWarmup: bSet.isWarmup || false,
            previousText: prevText
          };
        }

        return {
          ...bSet,
          isUnilateral: bSet.isUnilateral || (p ? p.isUnilateral : false),
          reps: '',
          weight: (p && p.isUnilateral) ? '' : maxWeight,
          weightL: maxWeightL,
          weightR: maxWeightR,
          repsL: '',
          repsR: '',
          isDropSet: bSet.isDropSet || false,
          isWarmup: bSet.isWarmup || false,
          previousText: prevText
        };
      });
    };

    const workoutState = {
      id: crypto.randomUUID(),
      routineId: routine.id,
      routineName: routine.name,
      startTime: Date.now(),
      profileId: currentProfile.id,
      name: routine.name,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
      feeling: 'normal',
      restTime: '',
      exercises: routine.exercises.map(block => {
        const type = block.type || 'single';
        
        if (type === 'superset') {
          return {
            type: 'superset',
            items: block.items.map(ex => {
              const prevSets = getPreviousPerformance(ex.exerciseId);
              return { exerciseId: ex.exerciseId, name: ex.name, equipment: ex.equipment, sets: buildInitialSets(ex, prevSets) };
            })
          };
        } else {
          const prevSets = getPreviousPerformance(block.exerciseId);
          return { type: 'single', exerciseId: block.exerciseId, name: block.name, equipment: block.equipment, sets: buildInitialSets(block, prevSets) };
        }
      })
    };
    
    setActiveWorkout(workoutState);
    setView('logger');
  };

  const updateSet = (blockIndex, itemIndex, setIndex, field, value) => {
    const updated = { ...activeWorkout };
    const block = updated.exercises[blockIndex];
    if (block.type === 'superset') {
      block.items[itemIndex].sets[setIndex][field] = value;
    } else {
      block.sets[setIndex][field] = value;
    }
    setActiveWorkout(updated);
  };

  const toggleComplete = (blockIndex, itemIndex) => {
    const updated = { ...activeWorkout };
    const block = updated.exercises[blockIndex];
    if (block.type === 'superset' && itemIndex !== null) {
      block.items[itemIndex].isCompleted = !block.items[itemIndex].isCompleted;
    } else {
      block.isCompleted = !block.isCompleted;
    }
    setActiveWorkout(updated);
  };

  const addSet = (blockIndex, itemIndex, isDrop, isUnilateral = false, isWarmup = false) => {
    const updated = { ...activeWorkout };
    const block = updated.exercises[blockIndex];
    let target = block.type === 'superset' ? block.items[itemIndex] : block;
    const isCardio = isCardioExercise(target, allExercises);
    const cardioType = isCardio ? getCardioType(target.name) : 'generic';
    
    const newSet = { 
      cardioType: cardioType,
      isUnilateral: isCardio ? false : isUnilateral, 
      isDropSet: isCardio ? false : isDrop, 
      isWarmup: isWarmup,
      previousText: null 
    };

    let insertIndex = target.sets.length;
    if (isWarmup) {
      insertIndex = 0;
      for (let i = 0; i < target.sets.length; i++) {
        if (target.sets[i].isWarmup) {
          insertIndex = i + 1;
        } else {
          break;
        }
      }
    }

    const previousSetForCopy = insertIndex > 0 ? target.sets[insertIndex - 1] : null;

    if (isCardio) {
      newSet.duration = '';
      newSet.distance = previousSetForCopy ? (previousSetForCopy.distance || previousSetForCopy.weight || '') : '';
      newSet.incline = previousSetForCopy ? (previousSetForCopy.incline || '') : '';
      newSet.speed = previousSetForCopy ? (previousSetForCopy.speed || '') : '';
      newSet.intensity = previousSetForCopy ? (previousSetForCopy.intensity || '') : '';
      newSet.floors = previousSetForCopy ? (previousSetForCopy.floors || '') : '';
      newSet.calories = '';
      newSet.reps = '';
      newSet.weight = '';
    } else if (isUnilateral) {
      newSet.weightL = previousSetForCopy ? (previousSetForCopy.weightL || previousSetForCopy.weight || '') : '';
      newSet.weightR = previousSetForCopy ? (previousSetForCopy.weightR || previousSetForCopy.weight || '') : '';
      newSet.repsL = '';
      newSet.repsR = '';
    } else {
      newSet.weight = previousSetForCopy ? (previousSetForCopy.weight || previousSetForCopy.weightL || '') : '';
      newSet.reps = '';
    }

    target.sets.splice(insertIndex, 0, newSet);
    setActiveWorkout(updated);
  };

  const removeSet = (blockIndex, itemIndex, setIndex) => {
    const updated = { ...activeWorkout };
    const block = updated.exercises[blockIndex];
    if (block.type === 'superset') {
      block.items[itemIndex].sets.splice(setIndex, 1);
    } else {
      block.sets.splice(setIndex, 1);
    }
    setActiveWorkout(updated);
  };

  const finishWorkout = async () => {
    // Clean up empty sets
    const cleanedExercises = activeWorkout.exercises.map(block => {
      if (block.type === 'superset') {
        const cleanedItems = block.items.map(item => {
          const isCardio = isCardioExercise(item, allExercises);
          const validSets = item.sets.filter(s => {
            if (isCardio) {
              return (s.duration !== '' && s.duration !== undefined) || (s.distance !== '' && s.distance !== undefined) || (s.reps !== '' && s.reps !== undefined) || (s.weight !== '' && s.weight !== undefined);
            }
            return s.isUnilateral ? (s.repsL !== '' && s.weightL !== '') : (s.reps !== '' && s.weight !== '');
          }).map(s => {
            if (isCardio) {
              const dur = s.duration !== undefined && s.duration !== '' ? s.duration : (s.reps || '');
              const dist = s.distance !== undefined && s.distance !== '' ? s.distance : (s.weight || '');
              return {
                duration: dur.toString().replace(',', '.'),
                distance: dist.toString().replace(',', '.'),
                calories: s.calories || '',
                isWarmup: s.isWarmup || false,
                isDropSet: false,
                isUnilateral: false,
                reps: dur.toString().replace(',', '.'),
                weight: dist.toString().replace(',', '.')
              };
            }
            if (s.isUnilateral) {
              return { isUnilateral: true, repsL: parseFloat(s.repsL.toString().replace(',', '.')), repsR: parseFloat(s.repsR.toString().replace(',', '.')), weightL: parseFloat(s.weightL.toString().replace(',', '.')), weightR: parseFloat(s.weightR.toString().replace(',', '.')), isDropSet: s.isDropSet || false, isWarmup: s.isWarmup || false };
            }
            return { isUnilateral: false, reps: parseFloat(s.reps.toString().replace(',', '.')), weight: parseFloat(s.weight.toString().replace(',', '.')), isDropSet: s.isDropSet || false, isWarmup: s.isWarmup || false };
          });
          return { ...item, sets: validSets };
        }).filter(item => item.sets.length > 0);
        return { ...block, items: cleanedItems };
      } else {
        const isCardio = isCardioExercise(block, allExercises);
        const cleanedSets = block.sets.filter(s => {
          if (isCardio) {
            return (s.duration !== '' && s.duration !== undefined) || (s.distance !== '' && s.distance !== undefined) || (s.reps !== '' && s.reps !== undefined) || (s.weight !== '' && s.weight !== undefined);
          }
          return s.isUnilateral ? (s.repsL !== '' && s.weightL !== '') : (s.reps !== '' && s.weight !== '');
        }).map(s => {
          if (isCardio) {
            const dur = s.duration !== undefined && s.duration !== '' ? s.duration : (s.reps || '');
            const dist = s.distance !== undefined && s.distance !== '' ? s.distance : (s.weight || '');
            return {
              duration: dur.toString().replace(',', '.'),
              distance: dist.toString().replace(',', '.'),
              calories: s.calories || '',
              isWarmup: s.isWarmup || false,
              isDropSet: false,
              isUnilateral: false,
              reps: dur.toString().replace(',', '.'),
              weight: dist.toString().replace(',', '.')
            };
          }
          if (s.isUnilateral) {
            return { isUnilateral: true, repsL: parseFloat(s.repsL.toString().replace(',', '.')), repsR: parseFloat(s.repsR.toString().replace(',', '.')), weightL: parseFloat(s.weightL.toString().replace(',', '.')), weightR: parseFloat(s.weightR.toString().replace(',', '.')), isDropSet: s.isDropSet || false, isWarmup: s.isWarmup || false };
          }
          return { isUnilateral: false, reps: parseFloat(s.reps.toString().replace(',', '.')), weight: parseFloat(s.weight.toString().replace(',', '.')), isDropSet: s.isDropSet || false, isWarmup: s.isWarmup || false };
        });
        return { ...block, sets: cleanedSets };
      }
    }).filter(block => block.type === 'superset' ? block.items.length > 0 : block.sets.length > 0);

    if (cleanedExercises.length === 0) {
      toast.error("La séance est vide !");
      return;
    }

    // Check for new PRs
    const prsToCelebrate = [];
    cleanedExercises.forEach(block => {
      const itemsToCheck = block.type === 'superset' ? block.items : [block];
      itemsToCheck.forEach(ex => {
        if (isCardioExercise(ex, allExercises)) return;
        const oldPRData = getExercisePR(ex.exerciseId);
        const oldPR = oldPRData.weight;
        let maxWeightInThisWorkout = 0;
        ex.sets.forEach(s => {
          if (s.isWarmup) return;
          if (s.isUnilateral) {
            const wL = parseFloat(s.weightL) || 0;
            const rL = parseInt(s.repsL) || 0;
            const wR = parseFloat(s.weightR) || 0;
            const rR = parseInt(s.repsR) || 0;
            if (rL >= 5 && wL > maxWeightInThisWorkout) maxWeightInThisWorkout = wL;
            if (rR >= 5 && wR > maxWeightInThisWorkout) maxWeightInThisWorkout = wR;
          } else {
            const w = parseFloat(s.weight) || 0;
            const r = parseInt(s.reps) || 0;
            if (r >= 5 && w > maxWeightInThisWorkout) maxWeightInThisWorkout = w;
          }
        });
        if (maxWeightInThisWorkout > oldPR) {
          prsToCelebrate.push({
            name: ex.name,
            oldPR: oldPR,
            newPR: maxWeightInThisWorkout
          });
        }
      });
    });

    const duration = activeWorkout.startTime ? Math.max(1, Math.round((Date.now() - activeWorkout.startTime) / 60000)) : null;

    const badgesBefore = getEarnedBadges(pastWorkouts, [], currentProfile);
    const completedWorkout = {
      ...activeWorkout,
      exercises: cleanedExercises,
      duration,
      timestamp: Date.now()
    };
    completedWorkout.totalVolume = calculateWorkoutVolume(completedWorkout);

    setSaving("Génération de l'analyse IA (cela peut prendre quelques secondes)...");
    try {
      const aiInsight = await generateWorkoutAnalysis(completedWorkout, currentProfile, pastWorkouts);
      if (aiInsight) {
        completedWorkout.aiInsight = aiInsight;
        setLastAiInsight(aiInsight);
      } else {
        setLastAiInsight("Le Coach IA fait une pause technique (serveurs saturés). Ta séance est bien enregistrée !");
      }
    } catch (e) {
      console.error("Erreur IA:", e);
      setLastAiInsight("Le Coach IA fait une pause technique (serveurs saturés ou quota dépassé). Ta séance est bien enregistrée !");
    }

    const badgesAfter = getEarnedBadges([...pastWorkouts, completedWorkout], [], currentProfile);
    const alreadyAwardedIds = new Set([
      ...badgesBefore.map(b => b.id),
      ...pastWorkouts.flatMap(w => (w.unlockedBadges || []).map(b => (typeof b === 'object' ? b.id : b)).filter(Boolean)),
      ...(Array.isArray(currentProfile?.badges) ? currentProfile.badges : [])
    ]);
    const unlockedBadges = badgesAfter.filter(b => !alreadyAwardedIds.has(b.id));

    let xpToAward = 0;
    const todayStr = new Date().toISOString().split('T')[0];
    const workoutsToday = pastWorkouts.filter(w => w.date && new Date(w.date).toISOString().split('T')[0] === todayStr).length;
    
    // TODO: Remettre à 15 après les tests de la boutique
    if (duration && duration >= 0) {
      if (workoutsToday < 2) {
        xpToAward += 50; // 50 XP de base
      } else {
        setTimeout(() => toast.info("Limite de 2 séances rémunérées par jour atteinte.", { icon: 'ℹ️' }), 1000);
      }
    } else {
      setTimeout(() => toast.info("Séance < 15 min : pas d'XP de base gagné.", { icon: 'ℹ️' }), 1000);
    }
    
    if (unlockedBadges.length > 0) {
      xpToAward += unlockedBadges.length * 35; // 35 XP bonus par nouveau succès
      completedWorkout.unlockedBadges = unlockedBadges.map(b => ({ id: b.id, name: b.name, description: b.description })); setNewBadges(unlockedBadges);
    }

    // --- MASCOT BONUS (10%) ---
    let hasMascotBonus = false;
    if (currentProfile?.equipped?.mascot) {
      xpToAward = Math.ceil(xpToAward * 1.1);
      hasMascotBonus = true;
    }

    setSaving("Enregistrement...");
    try {
      await db.saveWorkout(completedWorkout);
      if (unlockedBadges.length > 0) {
        await db.addBadgesToProfile(currentProfile.id, unlockedBadges.map(b => b.id));
      }
      if (xpToAward > 0) {
        await db.addXP(currentProfile.id, xpToAward);
        
        // --- PHASE 1: ECONOMY ---
        let coinsToAward = Math.floor(xpToAward / 10);
        if (coinsToAward > 0) {
          await db.addCurrency(currentProfile.id, coinsToAward, 0);
          const mascotText = hasMascotBonus ? " (Bonus Mascotte inclus !)" : "";
          setTimeout(() => toast.success(`+${coinsToAward} Pièces gagnées !${mascotText}`, { icon: '🟡' }), 500);
        }
      }

      // --- WORLD BOSS RAID CONTRIBUTION ---
      try {
        const bossRes = await db.recordWorkoutBossDamage(currentProfile, completedWorkout);
        if (bossRes?.damageDealt > 0) {
          setTimeout(() => {
            const unit = bossRes.damageUnit || 'dégâts';
            if (bossRes.isDefeated) {
              toast.success(`🎉 BOSS TERRASSÉ ! ${bossRes.isKiller ? "Tu as porté le coup de grâce à " : "La communauté a vaincu "} ${bossRes.bossName} ! (+${bossRes.damageDealt.toLocaleString()} ${unit})`, { icon: '👑' });
            } else {
              toast.success(`⚔️ Raid Boss : ${bossRes.damageDealt.toLocaleString()} ${unit} infligé(s) à ${bossRes.bossName} !`, { icon: '🐉' });
            }
          }, 1200);
        }
      } catch (bossErr) {
        console.warn("Erreur contribution boss:", bossErr);
      }
      
      localStorage.removeItem(`activeWorkout_${currentProfile.id}`);
      setActiveWorkout(null);
      setSaving(false);
      
      setNewPRs(prsToCelebrate);
      setView('celebration');
    } catch (error) {
      console.error("Erreur d'enregistrement:", error);
      setSaving(false);
      alert("Erreur lors de la sauvegarde. Si tu utilises un bloqueur de publicités (Adblock, Brave Shields...), il bloque peut-être la connexion à la base de données. Désactive-le pour ce site.");
    }
  };

  if (loading) return <Loader message="Chargement de tes programmes..." />;

  // VIEW: LIST OF ROUTINES
  if (view === 'list') {
    return (
      <div>
        <div className="flex justify-between items-center mb-4" style={{ marginBottom: '2.5rem' }}>
          <h2>Programmes <span className="text-secondary hidden-mobile" style={{ fontSize: '1rem', fontWeight: 'normal' }}>(Smart Log)</span></h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn hidden-mobile" style={{ backgroundColor: 'rgba(162, 155, 254, 0.1)', color: 'var(--primary-color)', border: '1px solid var(--primary-color)' }} onClick={() => setShowSmartBuilder(true)}>
              <Sparkles size={18} /> Générer avec l'IA
            </button>
            <button className="btn btn-icon btn-primary" style={{ display: 'none' }} id="mobile-ai-btn" onClick={() => setShowSmartBuilder(true)}>
              <Sparkles size={20} />
            </button>
            <style>{`
              @media (max-width: 768px) {
                #mobile-ai-btn { display: flex !important; background-color: rgba(162, 155, 254, 0.1) !important; color: var(--primary-color) !important; border: 1px solid var(--primary-color) !important; }
              }
            `}</style>
            <button className="btn btn-primary" onClick={() => {
              setEditingRoutineId(null);
              setNewRoutineName('');
              setSelectedExercises([]);
              setView('create');
            }}>
              <Plus size={20} /> <span className="hidden-mobile">Créer un Programme</span>
            </button>
          </div>
        </div>

        {routines.length === 0 ? (
          <div className="card text-center" style={{ padding: '3rem 1rem', opacity: 0.7 }}>
            <ListChecks size={48} style={{ margin: '0 auto', marginBottom: '1rem', color: 'var(--text-secondary)' }} />
            <p>Tu n'as pas encore de programme prédéfini.</p>
            <p style={{ fontSize: '0.9rem' }}>Crée-en un pour accélérer ta saisie lors de la prochaine séance !</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {routines.map(routine => (
              <div key={routine.id} className="card" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="flex justify-between items-center mb-3 border-b border-border-color pb-2">
                  <h3 style={{ margin: 0 }}>{routine.name}</h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn-icon" onClick={() => startEditRoutine(routine)}>
                      <Edit2 size={18} color="var(--primary-color)" />
                    </button>
                    <button className="btn-icon" onClick={() => handleShareRoutine(routine)} title="Partager sur le forum" style={{ color: 'var(--primary-color)' }}>
                      <Share2 size={18} />
                    </button>
                    <button className="btn-icon" onClick={() => {
                      confirmDialog('Supprimer ce programme ?', () => {
                        db.deleteRoutine(routine.id).then(() => {
                          loadData();
                          toast.success("Programme supprimé.");
                        });
                      });
                    }} title="Supprimer">
                      <Trash2 size={18} color="var(--danger-color)" />
                    </button>
                  </div>
                </div>
                <div className="mb-3" style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', flex: 1 }}>
                  {routine.exercises.map(e => e.type === 'superset' ? e.items.map(i => i.name).join(' + ') : (e.name || 'Ancien format')).join(', ')}
                </div>
                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%', backgroundColor: 'var(--secondary-color)', color: 'var(--bg-color)', marginTop: 'auto' }}
                  onClick={() => startRoutine(routine)}
                >
                  <Play size={18} /> Démarrer la séance
                </button>
              </div>
            ))}
          </div>
        )}

        {showSmartBuilder && (
          <SmartBuilderModal 
            onClose={() => setShowSmartBuilder(false)}
            allExercises={allExercises}
            onSave={(routine) => {
              setNewRoutineName(routine.name);
              setSelectedExercises(routine.exercises);
              setEditingRoutineId(null);
              setView('create');
              setShowSmartBuilder(false);
            }}
            onDirectSave={async (routine) => {
              try {
                await db.addRoutine({
                  profileId: currentProfile.id,
                  name: routine.name,
                  exercises: routine.exercises
                });
                toast.success('Programme IA enregistré avec succès !');
                loadData();
                setShowSmartBuilder(false);
              } catch (err) {
                console.error("Erreur save routine IA", err);
                toast.error('Erreur lors de la sauvegarde.');
              }
            }}
          />
        )}
      </div>
    );
  }

  if (view === 'create_old') {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4" style={{ marginBottom: '2.5rem' }}>
          <button className="btn-icon" onClick={() => {
            setView('list');
            setEditingRoutineId(null);
            setNewRoutineName('');
            setSelectedExercises([]);
          }}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0 }}>{editingRoutineId ? 'Modifier le Programme' : 'Nouveau Programme'}</h2>
        </div>

        <form onSubmit={handleSaveRoutine} className="card">
          <div className="form-group" style={{ marginBottom: '2rem' }}>
            <label className="form-label">Nom du Programme</label>
            <input 
              type="text" 
              className="form-control" 
              placeholder="Ex: Séance Haut du Corps" 
              value={newRoutineName}
              onChange={e => setNewRoutineName(e.target.value)}
              required
            />
          </div>

          <h4 className="mb-2">Ordre de la séance :</h4>
          <div style={{ marginBottom: '2rem' }}>
            {selectedExercises.length === 0 && <p style={{ color: 'var(--text-muted)' }}>Aucun exercice sélectionné. Piochez dans la liste ci-dessous !</p>}
            
            {selectedExercises.map((block, index) => (
              <div key={block.id} style={{ 
                backgroundColor: block.type === 'superset' ? 'rgba(168, 159, 207, 0.1)' : 'var(--bg-color)',
                border: block.type === 'superset' ? '2px solid var(--secondary-color)' : '1px solid var(--border-color)',
                borderRadius: 'var(--border-radius)',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div style={{ fontWeight: 'bold', color: block.type === 'superset' ? 'var(--secondary-color)' : 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {block.type === 'superset' ? `⚡ Superset (${block.items.length} exercices)` : <>{index + 1}. {block.name}{block.equipment && block.equipment !== 'Autre' ? <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.9em' }}> ({block.equipment})</span> : ''}</>}
                    {block.type !== 'superset' && (
                      <button type="button" className="btn-icon" style={{ padding: 0 }} onClick={() => setPreviewAddedExercise(allExercises.find(e => e.id === block.exerciseId || (e.name === block.name && e.equipment === block.equipment)) || block)}>
                        <Info size={16} color="var(--text-secondary)" />
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {index > 0 && <button type="button" className="btn-icon" onClick={() => moveBlockUp(index)}>↑</button>}
                    {index < selectedExercises.length - 1 && <button type="button" className="btn-icon" onClick={() => moveBlockDown(index)}>↓</button>}
                    <button type="button" className="btn-icon" onClick={() => removeSelectedBlock(index)}><Trash2 size={16} color="var(--danger-color)"/></button>
                  </div>
                </div>

                {block.type === 'superset' && (
                  <div>
                    {block.items.map((item, i) => (
                      <div key={i} style={{ padding: '0.5rem 0', borderBottom: i < block.items.length - 1 ? '1px dashed var(--border-color)' : 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        - {item.name}{item.equipment && item.equipment !== 'Autre' ? <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.9em' }}> ({item.equipment})</span> : ''}
                        <button type="button" className="btn-icon" style={{ padding: 0 }} onClick={() => setPreviewAddedExercise(allExercises.find(e => e.id === item.exerciseId || (e.name === item.name && e.equipment === item.equipment)) || item)}>
                          <Info size={16} color="var(--text-secondary)" />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', marginTop: '0.5rem', borderColor: 'var(--danger-color)', color: 'var(--danger-color)' }} onClick={() => unlinkSuperset(index)}>
                      Délier le superset
                    </button>
                  </div>
                )}

                {block.type !== 'superset' && index > 0 && (
                  <button type="button" className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', marginTop: '0.5rem' }} onClick={() => linkToPrevious(index)}>
                    🔗 Lier au précédent (Superset)
                  </button>
                )}
              </div>
            ))}
          </div>

          <h4 className="mb-2">Ajouter des exercices :</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            {Array.from(new Set([...MUSCLE_CATEGORIES, ...allExercises.flatMap(e => getMuscles(e))]))
              .filter(cat => cat !== 'Autre' && cat !== 'Bras' && cat !== 'Dos inférieur' && cat !== 'Triceps sural' && cat !== 'Abdominaux' && cat !== 'Jambes')
              .map(cat => {
              const isActive = muscleFilter.includes(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    if (isActive) setMuscleFilter(muscleFilter.filter(m => m !== cat));
                    else setMuscleFilter([...muscleFilter, cat]);
                  }}
                  style={{
                    padding: '0.4rem 0.8rem',
                    borderRadius: '20px',
                    border: `1px solid ${isActive ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    backgroundColor: isActive ? 'var(--primary-color)' : 'var(--bg-color)',
                    color: isActive ? '#fff' : 'var(--text-secondary)',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    fontWeight: isActive ? 'bold' : 'normal'
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius-sm)', padding: '0.5rem', marginBottom: '1rem' }}>
            {(muscleFilter.length > 0 ? allExercises.filter(ex => getMuscles(ex).some(m => muscleFilter.includes(m))) : allExercises).map(ex => {
              return (
                <div 
                  key={ex.id} 
                  style={{ 
                    padding: '0.8rem', 
                    borderBottom: '1px solid var(--border-color)', 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  className="hover:bg-border-color"
                  onClick={() => setPreviewExercise(ex)}
                >
                  <span>
                    {ex.name} 
                    {ex.equipment && ex.equipment !== 'Autre' && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 'normal', marginLeft: '0.5rem' }}>({ex.equipment})</span>}
                  </span>
                  <button type="button" className="btn-icon" onClick={(e) => { e.stopPropagation(); addExerciseToSelection(ex); }}>
                    <Plus size={20} color="var(--primary-color)" />
                  </button>
                </div>
              );
            })}
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
            Enregistrer le Programme
          </button>
        </form>

        {previewExercise && (
          <ExercisePreviewModal 
            exercise={previewExercise} 
            onClose={() => setPreviewExercise(null)} 
            onAdd={(ex) => { addExerciseToSelection(ex); setPreviewExercise(null); }} 
          />
        )}
        {previewAddedExercise && (
          <ExercisePreviewModal 
            exercise={previewAddedExercise} 
            onClose={() => setPreviewAddedExercise(null)} 
          />
        )}
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4" style={{ marginBottom: '2.5rem' }}>
          <button className="btn-icon" onClick={() => {
            setView('list');
            setEditingRoutineId(null);
            setNewRoutineName('');
            setSelectedExercises([]);
          }}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0 }}>{editingRoutineId ? 'Modifier le Programme' : 'Nouveau Programme'}</h2>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSaveRoutine(e); }} className="card" style={{ padding: '2rem' }}>
          <div className="form-group" style={{ marginBottom: '2rem' }}>
            <label className="form-label" style={{ fontWeight: 'bold' }}>Nom du Programme</label>
            <input 
              type="text" 
              className="form-control" 
              placeholder="Ex: Séance Haut du Corps" 
              value={newRoutineName}
              onChange={e => setNewRoutineName(e.target.value)}
              required
              style={{ padding: '0.8rem' }}
            />
          </div>

          <h4 className="mb-3" style={{ fontWeight: 'bold' }}>Exercices :</h4>
          <div style={{ marginBottom: '2rem' }}>
            {selectedExercises.length === 0 && (
              <div style={{ padding: '3rem 1rem', textAlign: 'center', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius)', border: '2px dashed var(--border-color)', color: 'var(--text-muted)' }}>
                <ListChecks size={40} style={{ margin: '0 auto 1rem auto', opacity: 0.5, color: 'var(--primary-color)' }} />
                <p style={{ margin: '0 0 0.5rem 0' }}>Aucun exercice sélectionné.</p>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Commence par ajouter ton premier exercice !</p>
              </div>
            )}
            
            {selectedExercises.map((block, index) => (
              <React.Fragment key={block.id}>
                {index > 0 && block.type !== 'superset' && selectedExercises[index - 1].type !== 'superset' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginLeft: '1.5rem', marginTop: '-1.5rem', marginBottom: '-0.5rem', zIndex: 10, position: 'relative' }}>
                    <button type="button" className="btn-icon" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '50%', padding: '0.4rem', color: 'var(--primary-color)', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} onClick={() => linkToPrevious(index)} title="Lier en Superset avec l'exercice ci-dessus">
                      <Link size={16} />
                    </button>
                  </div>
                )}
              <div style={{ 
                position: 'relative',
                backgroundColor: block.type === 'superset' ? 'rgba(162, 155, 254, 0.03)' : 'var(--bg-card)',
                backgroundImage: block.type === 'superset' ? 'linear-gradient(to right, rgba(162, 155, 254, 0.1), transparent)' : 'none',
                border: block.type === 'superset' ? '1px solid rgba(162, 155, 254, 0.5)' : '1px solid var(--border-color)',
                borderRadius: 'var(--border-radius)',
                padding: '1.2rem',
                marginBottom: '1rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: block.type === 'superset' ? '1rem' : '0' }}>
                  <div style={{ fontWeight: 'bold', color: block.type === 'superset' ? 'var(--primary-color)' : 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    {block.type === 'superset' ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'uppercase', fontSize: '0.85rem', backgroundColor: 'var(--primary-color)', color: 'white', padding: '0.3rem 0.8rem', borderRadius: '20px', letterSpacing: '0.5px' }}>
                        ⚡ Superset
                      </span>
                    ) : (
                      <>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '1rem', width: '25px', textAlign: 'right' }}>{index + 1}.</span> 
                        {block.name}{block.equipment && block.equipment !== 'Autre' ? <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.85em' }}> ({block.equipment})</span> : ''}
                      </>
                    )}
                    {block.type !== 'superset' && (
                      <button type="button" className="btn-icon" style={{ padding: 0 }} onClick={() => setPreviewAddedExercise(allExercises.find(e => e.id === block.exerciseId || (e.name === block.name && e.equipment === block.equipment)) || block)}>
                        <Info size={20} color="var(--text-secondary)" />
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.2rem', marginRight: '0.5rem' }}>
                      {index > 0 && <button type="button" className="btn-icon" style={{ padding: '0.4rem', backgroundColor: 'var(--bg-color)', borderRadius: '50%' }} onClick={() => moveBlockUp(index)} title="Monter"><ArrowDown size={16} style={{ transform: 'rotate(180deg)' }} /></button>}
                      {index < selectedExercises.length - 1 && <button type="button" className="btn-icon" style={{ padding: '0.4rem', backgroundColor: 'var(--bg-color)', borderRadius: '50%' }} onClick={() => moveBlockDown(index)} title="Descendre"><ArrowDown size={16} /></button>}
                    </div>
                    <button type="button" className="btn-icon" style={{ padding: '0.4rem' }} onClick={() => removeSelectedBlock(index)} title="Supprimer">
                      <Trash2 size={20} color="var(--danger-color)"/>
                    </button>
                  </div>
                </div>

                {block.type === 'superset' && (
                  <div style={{ paddingLeft: '1.5rem', borderLeft: '2px dashed rgba(162, 155, 254, 0.4)', marginLeft: '1rem' }}>
                    {block.items.map((item, i) => (
                      <div key={i} style={{ padding: '0.8rem 0', display: 'flex', alignItems: 'center', gap: '0.8rem', borderBottom: i < block.items.length - 1 ? '1px dashed var(--border-color)' : 'none' }}>
                        <div style={{ width: '8px', height: '8px', backgroundColor: 'var(--primary-color)', borderRadius: '50%' }}></div>
                        {item.name}{item.equipment && item.equipment !== 'Autre' ? <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.85em' }}> ({item.equipment})</span> : ''}
                        <button type="button" className="btn-icon" style={{ padding: 0 }} onClick={() => setPreviewAddedExercise(allExercises.find(e => e.id === item.exerciseId || (e.name === item.name && e.equipment === item.equipment)) || item)}>
                          <Info size={18} color="var(--text-secondary)" />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn" style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', marginTop: '1rem', backgroundColor: 'rgba(231, 76, 60, 0.1)', color: 'var(--danger-color)', border: '1px solid rgba(231, 76, 60, 0.3)', borderRadius: '20px' }} onClick={() => unlinkSuperset(index)}>
                      Délier le superset
                    </button>
                  </div>
                )}
              </div>
              </React.Fragment>
            ))}
          </div>

          <button 
            type="button" 
            className="btn btn-outline" 
            style={{ width: '100%', padding: '1rem', borderStyle: 'dashed', borderWidth: '2px', borderColor: 'var(--primary-color)', color: 'var(--primary-color)', marginBottom: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', borderRadius: 'var(--border-radius)', backgroundColor: 'rgba(162, 155, 254, 0.05)' }}
            onClick={() => setShowExerciseSelector(true)}
          >
            <Plus size={20} /> Ajouter un exercice
          </button>

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={isSavingRoutine || !newRoutineName.trim() || selectedExercises.length === 0}
            style={{ 
              width: '100%', 
              padding: '1rem', 
              backgroundColor: 'var(--secondary-color)', 
              color: 'var(--bg-color)', 
              borderRadius: 'var(--border-radius)', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '0.5rem',
              opacity: (isSavingRoutine || !newRoutineName.trim() || selectedExercises.length === 0) ? 0.6 : 1,
              cursor: isSavingRoutine ? 'not-allowed' : 'pointer'
            }}
          >
            {isSavingRoutine ? (
              <>
                <div className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px', borderColor: 'var(--bg-color) transparent transparent' }}></div>
                Enregistrement en cours...
              </>
            ) : (
              <>
                <CheckCircle size={20} /> Enregistrer le Programme
              </>
            )}
          </button>
        </form>

        {previewExercise && (
          <ExercisePreviewModal 
            exercise={previewExercise} 
            isAlreadyAdded={selectedExercises.some(b => b.type === 'superset' ? b.items?.some(it => it.id === previewExercise.id || it.exerciseId === previewExercise.id) : (b.id === previewExercise.id || b.exerciseId === previewExercise.id))}
            onClose={() => setPreviewExercise(null)} 
            onAdd={(ex) => { addExerciseToSelection(ex); setPreviewExercise(null); setShowExerciseSelector(false); }} 
          />
        )}
        {previewAddedExercise && (
          <ExercisePreviewModal 
            exercise={previewAddedExercise} 
            isAlreadyAdded={true}
            onClose={() => setPreviewAddedExercise(null)} 
          />
        )}
        {showExerciseSelector && (
          <ExerciseSelectorModal 
            exercises={allExercises} 
            selectedExercises={selectedExercises}
            onClose={() => setShowExerciseSelector(false)} 
            onSelect={(ex) => { addExerciseToSelection(ex); setShowExerciseSelector(false); }} 
          />
        )}
      </div>
    );
  }

  // VIEW: SMART LOGGER
  if (view === 'logger' && activeWorkout) {
    const isTimerGreen = timerSeconds >= 120; // 2 minutes

    return (
      <>
      <div style={{ paddingBottom: '55px' }}>
        <style>{`
          @media (max-width: 455px) {
            .logger-ex-title {
              font-size: 0.95rem !important;
            }
            .logger-ex-title-container {
              flex: 1;
              min-width: 0; /* allows text truncation if needed */
            }
          }
        `}</style>
        <PlateCalculatorModal 
          isOpen={showPlateCalc} 
          onClose={() => setShowPlateCalc(false)} 
          initialWeight={plateCalcTargetWeight} 
          initialBarWeight={plateCalcBarWeight}
          exerciseId={plateCalcExerciseId}
        />
        
        {/* Floating Timer */}
        <div style={{
          position: 'fixed',
          bottom: '55px',
          right: '20px',
          backgroundColor: isTimerRunning ? (isTimerGreen ? 'var(--secondary-color)' : '#f39c12') : 'var(--bg-card)',
          color: isTimerRunning ? '#fff' : 'var(--primary-color)',
          borderRadius: '50px',
          padding: '0.8rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          boxShadow: isTimerRunning 
            ? `0 0 20px ${isTimerGreen ? 'rgba(46, 204, 113, 0.6)' : 'rgba(243, 156, 18, 0.6)'}` 
            : '0 4px 15px rgba(0,0,0,0.2)',
          border: '2px solid',
          borderColor: isTimerRunning ? 'transparent' : 'var(--primary-color)',
          zIndex: 1000,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontWeight: '900',
          fontSize: '1.2rem',
          letterSpacing: '1px'
        }} onClick={toggleTimer}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {isTimerRunning ? <Square size={20} fill="currentColor" /> : <Timer size={24} strokeWidth={2.5} />}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{isTimerRunning ? formatTime(timerSeconds) : 'Repos'}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <button 
            className="btn-icon" 
            style={{ 
              flexShrink: 0, 
              width: '36px', 
              height: '36px', 
              minWidth: '36px', 
              minHeight: '36px', 
              padding: 0,
              marginTop: '4px'
            }}
            onClick={() => {
              confirmDialog("Quitter sans sauvegarder la séance ?", () => {
                localStorage.removeItem(`activeWorkout_${currentProfile.id}`);
                setActiveWorkout(null);
                setView('list');
              });
            }}
            title="Quitter la séance"
          >
            <ArrowLeft size={19} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ 
              display: 'block', 
              fontSize: '0.75rem', 
              fontWeight: 700, 
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              marginBottom: '0.15rem'
            }}>
              Séance en cours
            </span>
            <h2 style={{ 
              color: 'var(--secondary-color)', 
              margin: 0, 
              fontSize: '1.6rem', 
              fontWeight: 700,
              lineHeight: 1.2,
              wordBreak: 'normal',
              overflowWrap: 'break-word',
              hyphens: 'none'
            }}>
              {activeWorkout.name}
            </h2>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.4rem', 
              marginTop: '0.35rem', 
              color: 'var(--text-secondary)', 
              fontSize: '0.82rem', 
              position: 'relative',
              cursor: 'pointer',
              padding: '0.2rem 0.55rem',
              borderRadius: '6px',
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid var(--border-color)',
              width: 'fit-content'
            }} title="Modifier la date ou l'heure de la séance">
              <Calendar size={13} style={{ color: 'var(--primary-color)', flexShrink: 0 }} />
              <span style={{ fontWeight: 500 }}>{formatWorkoutDate(activeWorkout.date)}</span>
              <input 
                type="datetime-local" 
                value={activeWorkout.date || ''}
                onChange={e => setActiveWorkout({...activeWorkout, date: e.target.value})} 
                style={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%', 
                  opacity: 0, 
                  cursor: 'pointer',
                  margin: 0,
                  padding: 0
                }}
              />
            </div>
          </div>
        </div>



        {activeWorkout.exercises.map((block, blockIndex) => {
          // Helper to calculate the display number (don't increment for drop sets or warmups)
          const getSetNumber = (sets, currentIndex) => {
            let count = 0;
            for (let i = 0; i <= currentIndex; i++) {
              if (!sets[i].isDropSet && !sets[i].isWarmup) count++;
            }
            return count;
          };

          const renderExerciseItem = (item, itemIndex, isInsideSuperset = false) => {
            const isCardio = isCardioExercise(item, allExercises);
            const cardioType = isCardio ? getCardioType(item.name) : 'generic';
            const cardioFields = isCardio ? getCardioFields(cardioType) : [];
            const prData = !isCardio ? getExercisePR(item.exerciseId) : { weight: 0 };
            
            return (
              <div key={itemIndex} style={{ 
                marginBottom: isInsideSuperset ? '1.5rem' : '0',
                borderBottom: (isInsideSuperset && itemIndex < block.items.length - 1) ? '1px dashed var(--border-color)' : 'none',
                paddingBottom: (isInsideSuperset && itemIndex < block.items.length - 1) ? '1.5rem' : '0',
                opacity: item.isCompleted ? 0.7 : 1, transition: 'opacity 0.2s'
              }}>
                <div className={`flex justify-between items-center ${item.isCompleted ? "mb-1" : "mb-2"}`}>
                  <div className="logger-ex-title-container" style={{ paddingRight: '0.5rem' }}>
                    <h3 className="logger-ex-title" style={{ margin: 0, fontSize: isInsideSuperset ? '1.1rem' : '1.3rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {item.name}{item.equipment && item.equipment !== 'Autre' ? <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.9em' }}> ({item.equipment})</span> : ''}
                      {item.isCompleted && <CheckCircle size={16} color="var(--secondary-color)" style={{ flexShrink: 0 }} />}
                    </h3>
                    {!isCardio && prData && prData.weight > 0 && <div style={{ fontSize: '0.85rem', color: 'var(--secondary-color)', fontWeight: 'bold', marginTop: '0.2rem' }}>🏆 Objectif à battre (PR) : {prData.weight} kg</div>}
                    {item.sets && item.sets[0] && item.sets[0].previousText && (
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                        <span style={{ fontStyle: 'normal' }}>🕰️</span> <span style={{ fontStyle: 'italic' }}>Dernière fois : {item.sets[0].previousText}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      className="btn-icon" 
                      style={{ padding: '0.4rem' }} 
                      onClick={() => setReplaceTarget({ blockIndex, itemIndex: isInsideSuperset ? itemIndex : null })}
                      title="Remplacer l'exercice"
                    >
                      <RefreshCw size={22} color="var(--text-secondary)" />
                    </button>
                    <button 
                      className="btn-icon" 
                      onClick={() => toggleComplete(blockIndex, isInsideSuperset ? itemIndex : null)}
                      style={{ color: item.isCompleted ? 'var(--secondary-color)' : 'var(--text-secondary)' }}
                      title={item.isCompleted ? "Modifier l'exercice" : "Valider l'exercice"}
                    >
                      <CheckCircle size={22} />
                    </button>
                  </div>
                </div>
              
                {!item.isCompleted ? (
                  <>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isCardio 
                        ? `55px ${cardioFields.map(() => '1fr').join(' ')} 42px` 
                        : '55px 1fr 1fr 42px 42px', 
                      gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem', color: 'var(--text-secondary)' 
                    }}>
                      <div></div>
                      {isCardio ? (
                        <>
                          {cardioFields.map((f, i) => <div key={i}>{f.label}</div>)}
                          <div></div>
                        </>
                      ) : (
                        <>
                          <div>Reps</div>
                          <div>Poids (kg)</div>
                          <div></div>
                        </>
                      )}
                      <div></div>
                    </div>

                    {item.sets.map((set, setIndex) => (
                      <div key={setIndex} style={{ 
                        display: 'flex', flexDirection: 'column', marginBottom: '0.8rem',
                        marginLeft: set.isDropSet ? '1.5rem' : '0',
                        paddingLeft: set.isDropSet ? '0.8rem' : '0',
                        borderLeft: set.isDropSet ? '3px solid var(--secondary-color)' : 'none'
                      }}>
                        {isCardio ? (
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <div style={{ 
                              width: '55px',
                              fontWeight: 'bold', 
                              color: set.isWarmup ? '#f39c12' : 'var(--primary-color)',
                              fontSize: set.isWarmup ? '0.85rem' : '1rem',
                              flexShrink: 0
                            }}>
                              {set.isWarmup ? <><Flame size={16} style={{ color: '#f39c12', verticalAlign: 'middle', marginRight: '4px' }} />Échauf.</> : `Série ${getSetNumber(item.sets, setIndex)}`}
                            </div>
                            
                            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: cardioFields.length >= 4 ? '1fr 1fr' : 'repeat(auto-fit, minmax(0, 1fr))', gap: '0.5rem' }}>
                              {cardioFields.map((field, fIdx) => (
                                <div key={fIdx} style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                  <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{field.label}</label>
                                  <input 
                                    type={field.type}
                                    step={field.step}
                                    inputMode="decimal"
                                    className="form-control" 
                                    placeholder={field.placeholder}
                                    value={set[field.key] !== undefined ? set[field.key] : ''}
                                    onChange={e => {
                                      updateSet(blockIndex, itemIndex, setIndex, field.key, e.target.value);
                                      if (field.key === 'duration') updateSet(blockIndex, itemIndex, setIndex, 'reps', e.target.value);
                                      if (field.key === 'distance') updateSet(blockIndex, itemIndex, setIndex, 'weight', e.target.value);
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            
                            <div style={{ width: '42px', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                              <button className="btn-icon" onClick={() => removeSet(blockIndex, itemIndex, setIndex)} style={{ color: 'var(--danger-color)', padding: '0.4rem' }}>
                                <X size={18} />
                              </button>
                            </div>
                          </div>

                        ) : set.isUnilateral ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '55px 1fr 1fr 42px 42px', gap: '0.5rem', alignItems: 'center' }}>
                              <div style={{ fontWeight: 'bold', color: set.isDropSet ? 'var(--secondary-color)' : (set.isWarmup ? '#f39c12' : 'var(--primary-color)'), fontSize: set.isDropSet || set.isWarmup ? '0.85rem' : '1rem' }}>
                                {set.isDropSet ? '↳ Dég.' : (set.isWarmup ? '🔥 Échauf.' : `Série ${getSetNumber(item.sets, setIndex)}`)} <span style={{fontSize: '0.8rem', color: 'var(--text-secondary)'}}>(G)</span>
                              </div>
                              <input type="number" inputMode="numeric" pattern="[0-9]*" className="form-control" placeholder="Reps G" value={set.repsL} onChange={e => updateSet(blockIndex, itemIndex, setIndex, 'repsL', e.target.value)} />
                              <input type="text" inputMode="decimal" className="form-control" placeholder="Poids G" value={set.weightL} onChange={e => updateSet(blockIndex, itemIndex, setIndex, 'weightL', e.target.value)} />
                              <button className="btn-icon" onClick={() => handleOpenPlateCalc(set.weightL, item.exerciseId)}>
                                <Calculator size={18} color="var(--primary-color)" />
                              </button>
                              <button className="btn-icon" onClick={() => removeSet(blockIndex, itemIndex, setIndex)}>
                                <X size={18} color="var(--danger-color)" />
                              </button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '55px 1fr 1fr 42px 42px', gap: '0.5rem', alignItems: 'center' }}>
                              <div style={{ textAlign: 'right', paddingRight: '0.5rem', fontWeight: 'bold', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                (D)
                              </div>
                              <input type="number" step="0.5" inputMode="decimal" className="form-control" placeholder="Reps D" value={set.repsR} onChange={e => updateSet(blockIndex, itemIndex, setIndex, 'repsR', e.target.value)} />
                              <input type="text" inputMode="decimal" className="form-control" placeholder="Poids D" value={set.weightR} onChange={e => updateSet(blockIndex, itemIndex, setIndex, 'weightR', e.target.value)} />
                              <button className="btn-icon" onClick={() => handleOpenPlateCalc(set.weightR, item.exerciseId)}>
                                <Calculator size={18} color="var(--primary-color)" />
                              </button>
                              <div className="btn-icon" style={{ visibility: 'hidden', padding: '0.5rem' }}>
                                <X size={18} />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: '55px 1fr 1fr 42px 42px', gap: '0.5rem', alignItems: 'center' }}>
                            <div style={{ 
                              fontWeight: 'bold', 
                              color: set.isDropSet ? 'var(--secondary-color)' : (set.isWarmup ? '#f39c12' : 'var(--primary-color)'),
                              fontSize: set.isDropSet || set.isWarmup ? '0.85rem' : '1rem'
                            }}>
                              {set.isDropSet ? '↳ Dégressive' : (set.isWarmup ? '🔥 Échauf.' : `Série ${getSetNumber(item.sets, setIndex)}`)}
                            </div>
                            <input 
                              type="number" 
                              step="0.5"
                              inputMode="decimal"
                              className="form-control" 
                              placeholder="Reps"
                              value={set.reps}
                              onChange={e => updateSet(blockIndex, itemIndex, setIndex, 'reps', e.target.value)}
                            />
                            <input 
                              type="text" 
                              inputMode="decimal"
                              className="form-control" 
                              placeholder="Poids"
                              value={set.weight}
                              onChange={e => updateSet(blockIndex, itemIndex, setIndex, 'weight', e.target.value)}
                            />
                            <button className="btn-icon" onClick={() => handleOpenPlateCalc(set.weight, item.exerciseId)}>
                              <Calculator size={18} color="var(--primary-color)" />
                            </button>
                            <button className="btn-icon" onClick={() => removeSet(blockIndex, itemIndex, setIndex)}>
                              <X size={18} color="var(--danger-color)" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {isCardio ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '1rem' }}>
                        <button className="btn btn-outline" style={{ padding: '0.6rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }} onClick={() => addSet(blockIndex, itemIndex, false, false, false)}>
                          <Plus size={16} /> Série
                        </button>
                        <button className="btn btn-outline" style={{ padding: '0.6rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }} onClick={() => addSet(blockIndex, itemIndex, false, false, true)}>
                          <Flame size={16} color="#f39c12" /> Échauf.
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginTop: '1rem' }}>
                        <button className="btn btn-outline" style={{ padding: '0.5rem 0.2rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }} onClick={() => addSet(blockIndex, itemIndex, false, false, false)}>
                          <Plus size={16} /> Série
                        </button>
                        <button className="btn btn-outline" style={{ padding: '0.5rem 0.2rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }} onClick={() => addSet(blockIndex, itemIndex, false, false, true)}>
                          <Flame size={16} color="#f39c12" /> Échauf.
                        </button>
                        <button className="btn btn-outline" style={{ padding: '0.5rem 0.2rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }} onClick={() => addSet(blockIndex, itemIndex, false, true, false)}>
                          <ArrowLeftRight size={16} /> Unilatéral
                        </button>
                        <button className="btn btn-outline" style={{ padding: '0.5rem 0.2rem', fontSize: '0.85rem', borderColor: 'var(--secondary-color)', color: 'var(--secondary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }} onClick={() => addSet(blockIndex, itemIndex, true, false, false)}>
                          <TrendingDown size={16} /> Dégressive
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius-sm)', textAlign: 'center', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    {item.sets.length} série{item.sets.length > 1 ? 's' : ''} complétée{item.sets.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            );
          };

          if (block.type === 'superset') {
            return (
              <div key={blockIndex} className="card" style={{ borderLeft: '4px solid var(--secondary-color)', marginBottom: '2.5rem', backgroundColor: 'rgba(168, 159, 207, 0.05)' }}>
                <div style={{ 
                  display: 'inline-block', 
                  backgroundColor: 'var(--secondary-color)', 
                  color: 'white', 
                  padding: '0.2rem 0.6rem', 
                  borderRadius: '12px', 
                  fontSize: '0.8rem', 
                  fontWeight: 'bold', 
                  marginBottom: '1rem',
                  textTransform: 'uppercase'
                }}>
                  🔗 Superset
                </div>
                {block.items.map((item, itemIndex) => renderExerciseItem(item, itemIndex, true))}
              </div>
            );
          } else {
            return (
              <div key={blockIndex} className="card" style={{ borderLeft: '4px solid var(--primary-color)', marginBottom: '2.5rem' }}>
                {renderExerciseItem(block, null, false)}
              </div>
            );
          }
        })}

        <button 
          className="btn btn-outline" 
          style={{ width: '100%', padding: '0.8rem', fontSize: '1rem', marginBottom: '1rem', borderStyle: 'dashed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          onClick={() => setReplaceTarget({ isAdding: true })}
        >
          <Plus size={18} /> Ajouter un exercice
        </button>

        <button 
          className="btn btn-primary" 
          style={{ width: '100%', padding: '1rem', fontSize: '1.1rem', backgroundColor: 'var(--secondary-color)', color: 'var(--bg-color)' }}
          onClick={() => setShowFeelingModal(true)}
          disabled={saving}
        >
          {saving ? (typeof saving === 'string' ? saving : 'Enregistrement...') : 'Terminer la Séance'}
        </button>
      </div>

      {showFeelingModal && (
        <div 
          onClick={() => !saving && setShowFeelingModal(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div 
            className="card" 
            onClick={e => e.stopPropagation()} 
            style={{ width: '100%', maxWidth: '400px', padding: '2rem', display: 'flex', flexDirection: 'column' }}
          >
            {saving ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{ margin: '0 auto 1.5rem auto', width: '50px', height: '50px', border: '4px solid rgba(162, 155, 254, 0.3)', borderTopColor: 'var(--primary-color)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <h3 style={{ color: 'var(--primary-color)', marginBottom: '0.5rem' }}>Analyse en cours...</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{typeof saving === 'string' ? saving : "Sauvegarde de la séance..."}</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : (
              <>
                <h2 className="mb-4" style={{ textAlign: 'center', color: 'var(--primary-color)' }}>Comment s'est passée ta séance ?</h2>
                
                <div className="form-group mb-4">
                  <label className="form-label">Ressenti global</label>
                  <select 
                    className="form-control" 
                    value={activeWorkout.feeling || 'normal'}
                    onChange={e => setActiveWorkout({...activeWorkout, feeling: e.target.value})}
                    style={{ fontSize: '1.2rem', padding: '1rem' }}
                  >
                    <option value="easy">😊 Facile</option>
                    <option value="normal">😐 Normal</option>
                    <option value="hard">🥵 Difficile</option>
                    <option value="extreme">💀 Extrême</option>
                  </select>
                </div>
                
                <div className="form-group mb-4">
                  <label className="form-label">Repos moyen (min)</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Ex: 1:30 ou 2 (optionnel)" 
                    value={activeWorkout.restTime || ''}
                    onChange={e => setActiveWorkout({...activeWorkout, restTime: e.target.value})} 
                    style={{ fontSize: '1.1rem', padding: '0.8rem' }}
                  />
                </div>

                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%', padding: '1rem', fontSize: '1.1rem', backgroundColor: 'var(--secondary-color)', color: 'var(--bg-color)' }}
                  onClick={() => finishWorkout()}
                >
                  Sauvegarder et voir l'analyse
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {replaceTarget && (
        <ExerciseSelectorModal 
          exercises={allExercises} 
          selectedExercises={activeWorkout.exercises}
          onClose={() => setReplaceTarget(null)} 
          onSelect={handleReplaceExercise} 
          replaceTargetExercise={
            replaceTarget.isAdding 
              ? null 
              : replaceTarget.itemIndex != null
                ? activeWorkout.exercises[replaceTarget.blockIndex].items[replaceTarget.itemIndex]
                : activeWorkout.exercises[replaceTarget.blockIndex]
          }
        />
      )}
      </>
    );
  }

  
  // VIEW: CELEBRATION
  if (view === 'celebration') {
    return (
      <div className="card text-center" style={{ padding: '3rem 1rem', marginTop: '2rem' }}>
        <h2 style={{ color: 'var(--primary-color)', marginBottom: '1rem' }}>Séance Terminée !</h2>
        
        {lastAiInsight && (
          <div style={{
            backgroundColor: 'rgba(168, 159, 207, 0.1)',
            border: '1px solid var(--primary-color)',
            borderRadius: 'var(--border-radius-sm)',
            padding: '1.5rem',
            marginBottom: '2rem',
            textAlign: 'left',
            boxShadow: '0 4px 15px rgba(168, 159, 207, 0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.8rem', color: 'var(--primary-color)', fontWeight: 'bold', fontSize: '1.1rem' }}>
              ✨ Le Mot du Coach
            </div>
            <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.5', color: 'var(--text-primary)' }}>
              {lastAiInsight}
            </p>
          </div>
        )}

        {newPRs.length > 0 && (
          <>
            <h3 className="mb-4" style={{ color: 'var(--secondary-color)' }}>🏆 Nouveaux Records Personnels 🏆</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
              {newPRs.map((pr, i) => (
                <div key={i} style={{ backgroundColor: 'var(--bg-color)', padding: '1rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--secondary-color)', boxShadow: '0 4px 15px rgba(46, 204, 113, 0.2)' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.8rem' }}>{pr.name}</div>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
                    {pr.oldPR > 0 ? (
                      <span style={{ color: 'var(--text-secondary)', textDecoration: 'line-through', fontSize: '1.1rem' }}>{pr.oldPR} kg</span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.9rem' }}>Nouveau !</span>
                    )}
                    <ArrowRight size={24} color="var(--secondary-color)" />
                    <span style={{ fontWeight: '900', color: 'var(--secondary-color)', fontSize: '1.4rem' }}>{pr.newPR} kg</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {newBadges.length > 0 && (
          <>
            <h3 className="mb-4" style={{ color: 'var(--primary-color)' }}>🏆 Nouveaux Succès 🏆</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
              {newBadges.map((badge, i) => (
                <div key={i} style={{ backgroundColor: 'var(--bg-color)', padding: '1rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--primary-color)', boxShadow: '0 4px 15px rgba(168, 159, 207, 0.2)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ fontSize: '2rem' }}>{badge.icon || '🏆'}</div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'var(--primary-color)' }}>{badge.name || badge.title}</div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{badge.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <button className="btn btn-primary" style={{ width: '100%', padding: '1rem', fontSize: '1.1rem' }} onClick={() => navigate('/')}>
          Retour au Dashboard
        </button>
      </div>
    );
  }

  return null;
};

export default Routines;

